/**
 * Location-specific commercial natural gas end-use shares from Supabase
 * (`geography_commercial_gas_end_use`), built from CBECS 2018 + CBP establishment weights.
 */

import { supabaseClient } from './supabase';
import { DEFAULT_COMMERCIAL_GAS_END_USE } from './buildingEndUseAssumptions';

const DEFAULT_TOLERANCE = 0.03;

/** True if scenario commercial shares are still near app defaults (safe to replace with geography table). */
export function commercialSharesNearDefaults(
  c: { spaceHeating?: number; waterHeating?: number; cooking?: number; other?: number } | null | undefined
): boolean {
  if (!c) return true;
  const d = DEFAULT_COMMERCIAL_GAS_END_USE;
  return (
    Math.abs((c.spaceHeating ?? d.spaceHeating) - d.spaceHeating) < DEFAULT_TOLERANCE &&
    Math.abs((c.waterHeating ?? d.waterHeating) - d.waterHeating) < DEFAULT_TOLERANCE &&
    Math.abs((c.cooking ?? d.cooking) - d.cooking) < DEFAULT_TOLERANCE &&
    Math.abs((c.other ?? d.other) - d.other) < DEFAULT_TOLERANCE
  );
}

const METHODOLOGY_VERSION = 'cbecs2018_cbp_weighted_v1';

export interface CommercialGasEndUseRow {
  spaceHeating: number;
  waterHeating: number;
  cooking: number;
  other: number;
  electrifiableShare: number;
  establishmentDataYear: number;
}

function geofipsVariants(geofips: string): string[] {
  const raw = String(geofips).trim();
  const stripped = raw.replace(/^0+/, '') || '0';
  const out = new Set<string>([raw, stripped]);
  if (/^\d+$/.test(stripped)) {
    out.add(stripped.padStart(7, '0'));
  }
  return Array.from(out);
}

function variantSetsOverlap(a: string[], b: string[]): boolean {
  const set = new Set(a);
  return b.some((x) => set.has(x));
}

type RawCommercialGasRow = {
  geofips: string;
  space_heating_share: number;
  water_heating_share: number;
  cooking_share: number;
  other_share: number;
  electrifiable_share: number;
  establishment_data_year: number;
};

function pickBestCommercialGasRow(rows: RawCommercialGasRow[], requestedVariants: string[]): RawCommercialGasRow {
  const sorted = [...rows].sort((a, b) => {
    const y = b.establishment_data_year - a.establishment_data_year;
    if (y !== 0) return y;
    const aMatch = variantSetsOverlap(requestedVariants, geofipsVariants(String(a.geofips))) ? 1 : 0;
    const bMatch = variantSetsOverlap(requestedVariants, geofipsVariants(String(b.geofips))) ? 1 : 0;
    return bMatch - aMatch;
  });
  const bestYear = sorted[0].establishment_data_year;
  const sameYear = sorted.filter((r) => r.establishment_data_year === bestYear);
  const matched = sameYear.find((r) => variantSetsOverlap(requestedVariants, geofipsVariants(String(r.geofips))));
  return matched ?? sameYear[0];
}

/** Parent county `geofips` when `placeGeofips` is a City in `population`; otherwise null. */
async function resolveCountyGeofipsIfCity(placeGeofips: number): Promise<number | null> {
  const { data, error } = await supabaseClient
    .from('population')
    .select('geography_type, county')
    .eq('geofips', placeGeofips)
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  if (String(data.geography_type || '').trim() !== 'City') return null;

  const rawCounty = data.county != null ? String(data.county).trim() : '';
  const candidates: string[] = [];
  if (rawCounty) {
    candidates.push(rawCounty);
    if (!/\bCounty\b/i.test(rawCounty)) {
      candidates.push(`${rawCounty} County`);
    }
  }

  for (const name of candidates) {
    const { data: countyRow } = await supabaseClient
      .from('population')
      .select('geofips')
      .eq('location_name', name)
      .eq('geography_type', 'County')
      .limit(1)
      .maybeSingle();
    const g = countyRow?.geofips;
    if (g != null && Number.isFinite(Number(g))) return Number(g);
  }
  return null;
}

/**
 * Latest row for this geofips (any geography_type) matching methodology version.
 */
export async function fetchCommercialGasEndUseForGeofips(
  geofips: string | number
): Promise<CommercialGasEndUseRow | null> {
  const variants = geofipsVariants(String(geofips));
  const { data, error } = await supabaseClient
    .from('geography_commercial_gas_end_use')
    .select(
      'geofips, space_heating_share, water_heating_share, cooking_share, other_share, electrifiable_share, establishment_data_year, geography_type'
    )
    .in('geofips', variants)
    .eq('methodology_version', METHODOLOGY_VERSION)
    .order('establishment_data_year', { ascending: false })
    .limit(8);

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('fetchCommercialGasEndUseForGeofips:', error.message);
    }
    return null;
  }
  const rows = data as RawCommercialGasRow[] | null;
  if (!rows?.length) return null;
  const pick = pickBestCommercialGasRow(rows, variants);
  return {
    spaceHeating: Number(pick.space_heating_share),
    waterHeating: Number(pick.water_heating_share),
    cooking: Number(pick.cooking_share),
    other: Number(pick.other_share),
    electrifiableShare: Number(pick.electrifiable_share),
    establishmentDataYear: pick.establishment_data_year,
  };
}

/**
 * Same as {@link fetchCommercialGasEndUseForGeofips}, but if there is no row for a city geofips,
 * uses the parent county row (ETL often has county-only coverage in `business_establishments`).
 */
export async function fetchCommercialGasEndUseResolved(
  geofips: string | number
): Promise<CommercialGasEndUseRow | null> {
  const primary = typeof geofips === 'string' ? Number(geofips) : geofips;
  if (!Number.isFinite(primary)) return null;

  const direct = await fetchCommercialGasEndUseForGeofips(primary);
  if (direct) return direct;

  const countyG = await resolveCountyGeofipsIfCity(primary);
  if (countyG != null && countyG !== primary) {
    return fetchCommercialGasEndUseForGeofips(countyG);
  }
  return null;
}
